<script setup lang="ts" generic="TData extends RowData, TKey extends DeepKeys<TData>">
import { mdiClose, mdiInformationOutline } from "@mdi/js";
import type { Column, DeepKeys, DeepValue, RowData } from "@tanstack/vue-table";
import { useLocale } from "../../locale";
import Badge from "../badge/Badge.vue";
import { Button } from "../button";
import { Icon } from "../icon";
import { Tooltip } from "../tooltip";
import type { FacetedKeyOptions } from "./composables/faceted-types";
import { useFilterableColumn } from "./composables/filterable-column";

const props = defineProps<{
	column: Column<TData, DeepValue<TData, TKey>>;
	options?: FacetedKeyOptions<TData, TKey>;
	compact?: boolean;
	maxCount?: number;
}>();

const { t } = useLocale();
const { optionDisplay, selectedValues, selectedCount } = useFilterableColumn(
	() => props.column,
	() => props.options
);
</script>

<template>
	<Badge
		:class="{
			'text-base-content border-border h-auto py-1': true,
		}"
		color="primary"
		variant="soft"
	>
		<div class="flex flex-wrap items-center gap-2">
			<Tooltip v-if="selectedCount > (maxCount || Infinity)">
				<template #tooltip>
					<ul class="list">
						<li v-for="selected of selectedValues" :key="selected" class="list-row p-1">
							{{ optionDisplay(selected as any) }}
						</li>
					</ul>
				</template>

				<span :class="['font-semibold', compact && 'text-xs']">
					{{ selectedCount }} {{ t(selectedCount > 1 ? "selectedPlural" : "selected") }}
				</span>
			</Tooltip>
			<template v-else v-for="(selected, idx) of selectedValues" :key="idx">
				<span :class="['font-semibold', compact && 'text-xs']">
					{{ optionDisplay(selected as any) }}
				</span>

				<span v-if="idx !== selectedValues.length - 1" :class="compact && 'text-xs'">
					{{ t("or") }}
				</span>
			</template>
			<Tooltip v-if="options?.hint" :tooltip="options.hint">
				<Icon size="sm" :d="mdiInformationOutline" />
			</Tooltip>
		</div>
		<Button size="xs" variant="ghost" @click="column.setFilterValue(undefined)">
			<Icon :d="mdiClose" />
		</Button>
	</Badge>
</template>
